import { useEffect } from 'react';
import {
    GTAG_SCRIPT_ID,
    getOrCreateGtag,
    getTrackingWindow,
    injectHeadScript,
} from '../util/inject-head-script';

interface GA4Props {
    id: string;
    /** Signed-in member id. Omitted for guests. */
    userID?: string;
    /** Signed id used by GrowthBook: positive for members, negative for guests. */
    apUserID?: number;
}

export const GA4Head: React.FC<GA4Props> = ({ id, userID, apUserID }) => {
    // Sent as global params rather than `config` parameters, because `config`
    // sends the automatic `page_view`: re-configuring when a guest resolves to a
    // member — which login revalidation makes routine — would report a second
    // view of the same document. Declared ahead of the config effect so the ids
    // are already queued when that first `page_view` goes out, which `ga4Tracker`
    // relies on rather than sending `PageView` itself.
    useEffect(() => {
        getOrCreateGtag(getTrackingWindow())('set', {
            // Null rather than absent, so signing out clears the previous member
            // instead of leaving them attached to the rest of the session.
            user_id: userID ?? null,
            ...(apUserID === undefined ? {} : { ap_user_id: apUserID }),
        });
    }, [userID, apUserID]);

    // gtag is injected after hydration (see `injectHeadScript`); a gtag/js + inline
    // config in <head> would run during parse and break document hydration.
    useEffect(() => {
        const gtag = getOrCreateGtag(getTrackingWindow());
        gtag('js', new Date());
        gtag('config', id);

        injectHeadScript(
            `https://www.googletagmanager.com/gtag/js?id=${id}`,
            GTAG_SCRIPT_ID,
        );
    }, [id]);

    return null;
};
